import * as Sentry from '@sentry/react';

import { DYNAMIC_IMPORT_ERROR_PREFIXES } from 'client-web-sdk';

import getHeyloginVersion from './getHeyloginVersion';

export default function initSentry(skipProductionCheck = false) {
  // By default, only connect in production and staging, which both use production builds. The
  // hostname check is necessary to exclude review apps, since they also use the same prod build.
  const prodBuildEnv = import.meta.env.MODE === 'production';
  const prodOrStagingUrl =
    window.location.hostname.endsWith('heylogin.app') ||
    window.location.hostname.endsWith('staging.heylogin.dev');
  const isProdOrStaging = prodBuildEnv && prodOrStagingUrl;
  if (!isProdOrStaging && !skipProductionCheck) {
    return;
  }

  let environment;
  if (prodBuildEnv) {
    const isProd = window.location.hostname === 'heylogin.app';
    environment = isProd ? 'production' : 'staging';
  } else {
    environment = import.meta.env.MODE || 'unknown';
  }

  try {
    Sentry.init({
      dsn: 'https://810de5ea2a9d46eda69671169517cc3a@stackreports.heylogin.app/5336737',
      release: getHeyloginVersion() || 'unknown version',
      environment,
      // Disable auto session tracking, see https://docs.sentry.io/platforms/javascript/migration/v8-to-v9/#:~:text=To%20disable%20session%20tracking
      integrations: (integrations) => [
        ...integrations.filter((integration) => integration.name !== 'BrowserSession'),
        Sentry.zodErrorsIntegration(),
      ],
      ignoreErrors: [
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        // These happen on redeploy and get special treatment in FallbackComponent already
        ...DYNAMIC_IMPORT_ERROR_PREFIXES,
      ],
      beforeSend: (event) => {
        event.tags = {
          ...event.tags,
          usesChromeTranslation: !!document.querySelector(
            'html.translated-ltr, html.translated-rtl',
          ),
        };
        return event;
      },
    });
    if (!getHeyloginVersion() && !skipProductionCheck) {
      Sentry.captureMessage(
        'Sentry was initialized in a production build without version information',
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while initializing error reporting ¯\\_(ツ)_/¯', e);
  }
}
